import React, { useMemo } from 'react';
import { Formik } from 'formik';
import { FiAlertCircle } from 'react-icons/fi';

import DeleteButton from 'components/common/button/DeleteButton';
import { FormGroup, FormSelect } from 'components/common/form';
import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';

import { SKILLS } from 'constants/permissions';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import skillSchema from 'schemas/skillSchema';

import type { SelectOptionValue } from 'components/common/form/FormSelect';
import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type { LegacySkillCategory, SkillFormValues } from '../types';

type Props = {
  skill: SkillFormValues | null;
  handleSave: (skill: SkillFormValues, id?: number | null) => void;
  handleCancel: () => void;
  handleDelete: (skill: SkillFormValues) => void;
  saving: boolean;
  deleting: boolean;
  skillCategories: LegacySkillCategory[];
  isHr?: boolean;
};

const getInitialSkill = (skill: SkillFormValues | null): SkillFormValues => ({
  id: skill?.id,
  name: skill?.name || '',
  description: skill?.description || '',
  skillCategory: skill?.skillCategory || null,
});

const EditSkill: React.FC<Props> = ({
  skill,
  handleSave,
  handleCancel,
  handleDelete,
  saving,
  deleting,
  skillCategories,
}) => {
  const permissions = useUserPermissions() as PermissionMap;
  const selectedSkill = useMemo(() => getInitialSkill(skill), [skill]);

  const skillCategorySelectOptions = skillCategories.map((category) => ({ value: category, label: category.name }));

  return (
    <Formik<SkillFormValues>
      enableReinitialize
      initialValues={selectedSkill}
      validationSchema={skillSchema}
      onSubmit={(values) => handleSave(values, selectedSkill.id ?? null)}
    >
      {({ values, handleSubmit, errors, touched, handleChange, handleBlur, setFieldValue }) => (
        <form className="edit-skill card-form" onSubmit={handleSubmit}>
          <h3 className="edit-skill_header">Skill Details</h3>
          <FormGroup
            name="name"
            label="Skill Name"
            isMandatory={true}
            value={values.name}
            error={touched.name && errors.name ? errors.name : undefined}
            handleBlur={handleBlur}
            readOnly={!permissions[SKILLS.CREATE]}
            handleChange={handleChange}
            placeholder="Skill Name"
          />
          <FormGroup
            name="description"
            label="Skill Description"
            multiline={true}
            isMandatory={false}
            value={values.description ?? ''}
            readOnly={!permissions[SKILLS.CREATE]}
            error={touched.description && errors.description ? errors.description : undefined}
            handleBlur={handleBlur}
            handleChange={handleChange}
            placeholder="Skill Description"
          />
          {permissions[SKILLS.CREATE] && (
            <FormSelect
              label="Skill Category"
              name="skillCategory"
              isMandatory={true}
              handleChange={(event) => {
                void setFieldValue('skillCategory', event.target.value as SelectOptionValue | null);
              }}
              value={values.skillCategory ?? undefined}
              error={touched.skillCategory && errors.skillCategory ? 'Please enter the skill category' : undefined}
              options={skillCategorySelectOptions}
              uniqueIdentifier="id"
            />
          )}

          <div className="mt-30 button-area">
            {(permissions[SKILLS.CREATE] || permissions[SKILLS.UPDATE]) && (
              <button
                type="submit"
                disabled={saving}
                className="btn btn--primary f-left card-button mr-10 min-width-80"
              >
                {saving ? <Loading /> : selectedSkill.id ? 'Update' : 'Create'}
              </button>
            )}
            <button type="button" onClick={handleCancel} className="btn btn--outlined-grey f-left card-button">
              Cancel
            </button>
            {permissions[SKILLS.DELETE] && selectedSkill.id && (
              <LFModal
                TriggerBtn={DeleteButton}
                isLoading={deleting}
                modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                title={{ text: 'Do you want to delete this skill?', type: 'danger' }}
                className="delete-modal"
                message={{ text: 'This action cannot be reverted back once deleted.', type: 'delete__text' }}
                renderFooter={true}
                buttons={[
                  {
                    text: 'Yes, delete',
                    type: 'red',
                    className: 'mr-12 delete__btn',
                    onClick: () => handleDelete(selectedSkill),
                    close: true,
                    showLoading: true,
                  },
                  {
                    text: 'No, go back',
                    type: 'outlined-grey',
                    className: 'py-8 px-12',
                    close: true,
                  },
                ]}
              />
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default EditSkill;
